/**
 * Pure utility functions shared across asset summary UI components.
 */

import {
  isExplicitRocapSourceType,
  resolveRocapValuationIndex,
} from "@cvx/jobs/summarization/lib/rocapValuation";
import type { AssetEntryUI, ValuationGroup } from "./types";
import { normalizeSourceType, type FilterTab } from "./constants";
import type { SecuredNettingResult } from "./securedNetting";

export {
  isExplicitRocapSourceType,
  isRocapSourcedValuation,
  resolveRocapValuationIndex,
} from "@cvx/jobs/summarization/lib/rocapValuation";

// ---------------------------------------------------------------------------
// Currency formatting
// ---------------------------------------------------------------------------

export function formatCurrency(value: number): string {
  if (value === 0) return "$0";
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toLocaleString()}`;
}

// ---------------------------------------------------------------------------
// Time formatting
// ---------------------------------------------------------------------------

export function fmtTime(ts: number): string {
  return new Date(ts).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Valuation helpers
// ---------------------------------------------------------------------------

/**
 * Get the "best" value for an asset entry: uses selectedValuation if available,
 * otherwise falls back to the first valuation's value, or 0 if no valuations.
 */
export function getBestValue(entry: AssetEntryUI): number {
  if (entry.selectedValuation && entry.valuations.length > 0) {
    const v = entry.valuations[entry.selectedValuation.valuationIndex];
    return v?.value ?? 0;
  }
  if (entry.valuations.length > 0) {
    return entry.valuations[0].value ?? 0;
  }
  return 0;
}

/**
 * Get the selected valuation value (null if no selection).
 */
export function getSelectedValue(entry: AssetEntryUI): number | null {
  if (!entry.selectedValuation || entry.valuations.length === 0) return null;
  const v = entry.valuations[entry.selectedValuation.valuationIndex];
  return v?.value ?? null;
}

// ---------------------------------------------------------------------------
// Recovery-scenario selectors (shared by the asset table + outcome waterfall)
// ---------------------------------------------------------------------------

/**
 * The single ROCAP figure for an asset.
 *
 * Prefers a practitioner override, then the recovery agent's chosen index,
 * then a deterministic inference: an explicit `sourceType: "ROCAP"` or a
 * valuation whose source document/notes are a Form 507 / ROCAP annexure
 * (e.g. Annexure Part A4). Returns null when nothing is ROCAP-sourced
 * (⇒ blank column, $0 in the ROCAP waterfall scenario).
 */
export function getRocapValue(entry: AssetEntryUI): number | null {
  if (entry.rocapOverride) return entry.rocapOverride.value;
  const index = resolveRocapValuationIndex(
    entry.valuations,
    entry.chosenRocapValuationIndex,
  );
  if (index == null) return null;
  return entry.valuations[index]?.value ?? null;
}

/**
 * The valuation currently driving the ROCAP figure (override excluded).
 * Used by the detail panel to label "Director ROCAP" vs "Derived from ROCAP".
 */
export function getResolvedRocapValuation(
  entry: AssetEntryUI,
): AssetEntryUI["valuations"][number] | null {
  if (entry.rocapOverride) return null;
  const index = resolveRocapValuationIndex(
    entry.valuations,
    entry.chosenRocapValuationIndex,
  );
  if (index == null) return null;
  return entry.valuations[index] ?? null;
}

/** True when the resolved ROCAP figure came from an annexure, not Form 507 itself. */
export function isDerivedRocapValuation(
  valuation: AssetEntryUI["valuations"][number] | null,
): boolean {
  return valuation != null && !isExplicitRocapSourceType(valuation.sourceType);
}

/**
 * The practitioner's value for an asset — i.e. the valuation the practitioner
 * has **selected** (endorsed) as the figure for this asset, whatever its source
 * (financial statement, ROCAP, a hand-entered override, …). This is the
 * "Selected Value" shown in the detail panel, surfaced as the Practitioner
 * column + the Practitioner waterfall scenario. Returns null when nothing is
 * selected (⇒ blank column, $0 in that scenario).
 */
export function getPractitionerValue(entry: AssetEntryUI): number | null {
  return getSelectedValue(entry);
}

/** Conservative estimated realisable recovery (null when not yet estimated). */
export function getLowRecovery(entry: AssetEntryUI): number | null {
  return entry.estimatedRecovery?.low ?? null;
}

/** Optimistic estimated realisable recovery (null when not yet estimated). */
export function getHighRecovery(entry: AssetEntryUI): number | null {
  return entry.estimatedRecovery?.high ?? null;
}

/** The AI's single-point estimate of realisable value (null when not run). */
export function getAiEstimateValue(entry: AssetEntryUI): number | null {
  return entry.estimatedRecovery?.aiEstimate?.value ?? null;
}

// ---------------------------------------------------------------------------
// Full currency formatting (not abbreviated)
// ---------------------------------------------------------------------------

export function formatFullCurrency(value: number | null | undefined): string {
  if (value == null) return "—";
  return `$${value.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ---------------------------------------------------------------------------
// Secured-netting link status (why an asset can't show a net value yet)
// ---------------------------------------------------------------------------

/**
 * Why a secured asset has no calculable net value. Netting needs an asset
 * linked to a security interest AND that SI to have an owed figure (its own
 * amount, or a linked creditor):
 * - `no_si` — the asset is flagged secured but isn't linked to any security
 *   interest at all.
 * - `si_no_owed` — the asset IS linked to a security interest, but that SI has
 *   no owed amount at all (no amount entered on it, and no creditor linked), so
 *   there is nothing to net against.
 * `undefined` means either unsecured or fully netted — nothing to warn about.
 */
export type AssetSecurityLinkStatus = "no_si" | "si_no_owed";

export const SECURITY_LINK_WARNING: Record<
  AssetSecurityLinkStatus,
  { short: string; full: string }
> = {
  no_si: {
    short: "No SI linked",
    full: "Marked secured, but no security interest is linked. Link the security interest so the net value can be calculated.",
  },
  si_no_owed: {
    short: "No owed amount",
    full: "Linked to a security interest, but that security interest has no owed amount — nothing has been entered on it and no creditor is linked. Enter the amount owed on the security interest (or link the secured creditor) so the net value can be calculated.",
  },
};

/**
 * Derive, per asset, why its secured net value can't be computed (if at all).
 * Pure function of the active asset rows + the headline netting result, so it
 * stays consistent with the figures shown elsewhere.
 *
 * An asset is "encumbered" when it appears in at least one SI group (the group
 * set already includes SIs that have an asset but no creditor, with `owed: 0`).
 * - In a group whose combined `owed` is 0 → `si_no_creditor`.
 * - In no group but flagged secured → `no_si`.
 * - Otherwise omitted (unsecured, or netted with a real owed amount).
 */
export function computeSecurityLinkStatusByAsset(
  activeEntries: AssetEntryUI[],
  headline: SecuredNettingResult,
): Map<string, AssetSecurityLinkStatus> {
  // assetId → does any SI group it belongs to carry an owed amount?
  const groupOwedByAsset = new Map<string, boolean>();
  for (const g of headline.groups) {
    for (const aid of g.assetIds) {
      groupOwedByAsset.set(
        aid,
        (groupOwedByAsset.get(aid) ?? false) || g.owed > 0,
      );
    }
  }

  const status = new Map<string, AssetSecurityLinkStatus>();
  for (const e of activeEntries) {
    if (e.isExcluded) continue;
    if (groupOwedByAsset.has(e._id)) {
      // Linked to at least one SI. Warn only if none of them have an owed amount.
      if (!groupOwedByAsset.get(e._id)) status.set(e._id, "si_no_owed");
    } else if (e.subjectToSecurityInterest) {
      status.set(e._id, "no_si");
    }
  }
  return status;
}

// ---------------------------------------------------------------------------
// Short date formatting
// ---------------------------------------------------------------------------

export function formatShortDate(dateStr: string | undefined): string {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

// ---------------------------------------------------------------------------
// Selected valuation object
// ---------------------------------------------------------------------------

export function getSelectedValuationObj(entry: AssetEntryUI) {
  if (!entry.selectedValuation || entry.valuations.length === 0) return null;
  const v = entry.valuations[entry.selectedValuation.valuationIndex];
  return v ?? null;
}

// ---------------------------------------------------------------------------
// Format source type for display (snake_case → Title Case)
// ---------------------------------------------------------------------------

/**
 * Converts raw sourceType strings into human-readable labels.
 * Already-formatted strings (containing spaces) pass through unchanged;
 * "ROCAP" keeps its uppercase form. Delegates to the shared canonicaliser so
 * display labels always match the icon / reliability lookup keys.
 */
export function formatSourceType(sourceType: string): string {
  return normalizeSourceType(sourceType);
}

// ---------------------------------------------------------------------------
// Valuation-source columns (MOR-544)
// ---------------------------------------------------------------------------

/**
 * Distinct valuation sourceTypes across the given entries, alphabetical.
 * Drives the "Columns" eye-toggle menu on the Asset table — the menu is
 * fully dynamic, so a new source extracted tomorrow appears automatically.
 * Sources are canonicalised so "balance_sheet" (agent) and "Balance Sheet"
 * (practitioner) collapse into a single column.
 */
export function collectValuationSources(entries: AssetEntryUI[]): string[] {
  const sources = new Set<string>();
  for (const e of entries) {
    for (const valuation of e.valuations)
      sources.add(normalizeSourceType(valuation.sourceType));
  }
  return Array.from(sources).sort((a, b) => a.localeCompare(b));
}

/**
 * Valuation sources that must NOT get their own per-source breakdown column
 * (asset table, export, and the estimated-outcome waterfall).
 *
 * Both the raw "ROCAP" source and the raw "Director Estimate" source are
 * already represented by the resolved director figure that shows under the
 * dedicated ROCAP column/scenario (a director's annexure estimate is resolved
 * into ROCAP). Rendering them as extra breakdown columns duplicates that figure
 * and reads as a contradictory second director total, so they are hidden.
 */
export function isHiddenValuationSource(source: string): boolean {
  if (isExplicitRocapSourceType(source)) return true;
  return normalizeSourceType(source) === "Director Estimate";
}

/**
 * The single figure an asset contributes to a valuation-source column.
 * When an asset has several valuations from the same source, the latest
 * `asAtDate` wins; undated entries lose to dated ones, and an all-undated
 * set falls back to the last entry (the most recently added). Returns
 * null when the asset has no valuation from that source — the column
 * renders an em dash, distinguishing "no valuation" from "valued at $0".
 */
export function getValuationForSource(
  entry: AssetEntryUI,
  sourceType: string,
): number | null {
  const canonical = normalizeSourceType(sourceType);
  const best = entry.valuations.reduce<{
    value: number;
    dateKey: string;
  } | null>((current, v) => {
    if (normalizeSourceType(v.sourceType) !== canonical) return current;
    // ISO dates compare lexicographically; "" sorts below any real date so
    // undated entries lose. `>=` lets a later array entry win ties.
    const dateKey = v.asAtDate ?? "";
    if (current === null || dateKey >= current.dateKey) {
      return { value: v.value, dateKey };
    }
    return current;
  }, null);
  return best?.value ?? null;
}

// ---------------------------------------------------------------------------
// Filter assets by tab
// ---------------------------------------------------------------------------

export function filterAssets(
  assets: AssetEntryUI[],
  tab: FilterTab,
): AssetEntryUI[] {
  if (tab === "Excluded") {
    return assets.filter((a) => a.isExcluded);
  }

  const nonExcluded = assets.filter((a) => !a.isExcluded);

  switch (tab) {
    case "Flagged":
      return nonExcluded.filter(
        (a) => a.validationFlags && a.validationFlags.length > 0,
      );
    case "Secured":
      return nonExcluded.filter((a) => a.subjectToSecurityInterest);
    case "Unsecured":
      return nonExcluded.filter((a) => !a.subjectToSecurityInterest);
    case "Cash at Bank":
      return nonExcluded.filter((a) => a.assetType === "Cash at Bank");
    case "Accounts Receivable":
      return nonExcluded.filter((a) => a.assetType === "Accounts Receivable");
    case "Plant & Equipment":
      return nonExcluded.filter((a) => a.assetType === "Plant and Equipment");
    default:
      return nonExcluded;
  }
}

// ---------------------------------------------------------------------------
// Group valuations — merge ROCAP valuations with same value from different directors
// ---------------------------------------------------------------------------

/** The representative source type / as-at date for a valuation group. */
export function valuationGroupSourceType(group: ValuationGroup): string {
  return group.type === "single"
    ? group.valuation.sourceType
    : group.primary.sourceType;
}

export function valuationGroupAsAtDate(group: ValuationGroup): string {
  return (
    (group.type === "single"
      ? group.valuation.asAtDate
      : group.primary.asAtDate) ?? ""
  );
}

function valuationGroupIndex(group: ValuationGroup): number {
  return group.type === "single"
    ? group.originalIndex
    : group.originalIndices[0];
}

/**
 * A source document paired with the as-at date of the valuation it backs.
 * Same shape as a valuation's `sourceDocument`, plus the per-source date so
 * a merged card can show when each document reported the figure.
 */
export type ValuationSourceWithDate = NonNullable<
  AssetEntryUI["valuations"][number]["sourceDocument"]
> & { asAtDate?: string };

/**
 * Distinct source documents across a group of same-figure valuations, in
 * original order, each carrying its own as-at date. Dedupes by
 * name + section + date so two extracts of the same annexure line at the same
 * date don't list the file twice, while the same figure reported at two
 * different dates keeps both dated rows.
 */
export function uniqueValuationSources(
  valuations: AssetEntryUI["valuations"],
): ValuationSourceWithDate[] {
  const seen = new Set<string>();
  const sources: ValuationSourceWithDate[] = [];
  for (const v of valuations) {
    const doc = v.sourceDocument;
    if (!doc) continue;
    const key = `${doc.name}|${doc.section ?? ""}|${v.asAtDate ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    sources.push({ ...doc, asAtDate: v.asAtDate });
  }
  return sources;
}

/**
 * Same real-world figure: matching source type and dollar amount. The as-at
 * date is deliberately excluded so the same figure reported by two documents
 * at different dates (e.g. a $150k balance-sheet line at 30 Jun and 2 Aug)
 * collapses into one card; each source keeps its own date on its row.
 */
function sameFigureKey(v: AssetEntryUI["valuations"][number]): string {
  return `${normalizeSourceType(v.sourceType)}|${v.value}`;
}

export function groupValuations(
  valuations: AssetEntryUI["valuations"],
): ValuationGroup[] {
  const groups: ValuationGroup[] = [];
  const rocapsByValue: Record<
    string,
    {
      valuations: AssetEntryUI["valuations"][number][];
      indices: number[];
      directors: string[];
    }
  > = {};
  const figuresByKey: Record<
    string,
    {
      valuations: AssetEntryUI["valuations"][number][];
      indices: number[];
    }
  > = {};

  for (let i = 0; i < valuations.length; i++) {
    const v = valuations[i];
    if (normalizeSourceType(v.sourceType) === "ROCAP" && v.directorName) {
      const key = String(v.value);
      if (!rocapsByValue[key]) {
        rocapsByValue[key] = { valuations: [], indices: [], directors: [] };
      }
      rocapsByValue[key].valuations.push(v);
      rocapsByValue[key].indices.push(i);
      rocapsByValue[key].directors.push(v.directorName);
    } else {
      const key = sameFigureKey(v);
      if (!figuresByKey[key]) {
        figuresByKey[key] = { valuations: [], indices: [] };
      }
      figuresByKey[key].valuations.push(v);
      figuresByKey[key].indices.push(i);
    }
  }

  for (const key of Object.keys(rocapsByValue)) {
    const group = rocapsByValue[key];
    if (group.valuations.length > 1) {
      groups.push({
        type: "merged_rocap",
        primary: group.valuations[0],
        valuations: group.valuations,
        originalIndices: group.indices,
        directors: group.directors,
      });
    } else {
      groups.push({
        type: "single",
        valuation: group.valuations[0],
        originalIndex: group.indices[0],
      });
    }
  }

  for (const key of Object.keys(figuresByKey)) {
    const group = figuresByKey[key];
    if (group.valuations.length > 1) {
      groups.push({
        type: "merged_sources",
        primary: group.valuations[0],
        valuations: group.valuations,
        originalIndices: group.indices,
      });
    } else {
      groups.push({
        type: "single",
        valuation: group.valuations[0],
        originalIndex: group.indices[0],
      });
    }
  }

  groups.sort((a, b) => valuationGroupIndex(a) - valuationGroupIndex(b));

  return groups;
}
